import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ContainerLayout from '../../../../components/container-layout/ContainerLayout';
import { Box, Button } from '@mui/material';
import { ELibraryCategory, LIBRARY_HEAD_STATUS, LIBRARY_LIKE } from '../../enum/elibrary_enum';
import { TFunction, useTranslation } from 'react-i18next';
import NavigateListCustom from '../../../../components/navigation/NavigationListCustom';
import { routeName } from '../../../../routes/routeName';
import StateComponents from '../../../../components/state-component/StateComponents';
import { icons } from '../../../../assets/icons/icons';
import { debouncedApiCall } from '../../../../utils/app.utils';
import { library_type_list, LibraryTypeApi } from '../../../../api/library-type/library_type.api';
import { ELibraryFrontApi, listData } from '../../../../api/e-library/e_library_front.api';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, unsetLoading } from '../../../../store/loading/loading.slice';
import BookSlide from '../element/BooksViewList';
import { RootState } from '../../../../store/store';
import CustomPagination from '../../../../components/pagination/PaginationCustom';
import Hashids from 'hashids';
import BooksCart from '../element/BooksCart';
import { globalLikeBookmark } from '../element/global_like_func';
import AudioPopover from '../../../../components/audio/AudioPopover';
import { SwiperOptions } from 'swiper/types';
import { FilterSection } from './element/FilterOptionsCard';
import PopoverFilter from './element/FilterOptionsCard';
import { IconComponents } from '../../../../assets/icons/icons';
import i18n from '../../../../i18n';

export const status_data = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('หนังสือมาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('หนังสือยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('หนังสือทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_ebook = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('E-book มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('E-book ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('E-book ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_journal = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('วารสาร มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('วารสาร ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('วารสาร ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_audio_book = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('หนังสือเสียง มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('หนังสือเสียง ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('หนังสือเสียง ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_thesis = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('วิทยานิพนธ์ มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('วิทยานิพนธ์ ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('วิทยานิพนธ์ ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_podcast = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('พอดแคสต์ มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('พอดแคสต์ ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('พอดแคสต์ ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];
export const status_data_video = (t: TFunction<'translation', undefined>): { label: string; value: string }[] => [
    { label: t('วีดีโอ มาใหม่'), value: LIBRARY_HEAD_STATUS.NEW },
    { label: t('วีดีโอ ยอดนิยม'), value: LIBRARY_HEAD_STATUS.POPULAR },
    { label: t('วีดีโอ ทั้งหมด'), value: LIBRARY_HEAD_STATUS.ALL },
];

type Mode = 'NEW' | 'POPULAR' | 'ALL';


type Props = {
    category_label?: string;
    category_path?: string;
    library_category_name?: ELibraryCategory;
    category_pathname_label?: string;
    path_detail?: string;
    allow_shadow?: boolean;
    allow_view_audio?: boolean;
    breakpoints?: SwiperOptions['breakpoints'];
}


const BooksList: React.FC<Props> = ({
    library_category_name = ELibraryCategory.BOOK,
    category_label = i18n.t('หนังสือ') as string,
    category_path = routeName.e_library.books,
    allow_shadow,
    allow_view_audio,
    breakpoints,
    path_detail = routeName.e_library.books_detail
}) => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const statusParams = status_data(t);
    const statusEbookParams = status_data_ebook(t);
    const statusAudioBookParams = status_data_audio_book(t);
    const statusJournalParams = status_data_journal(t);
    const statusThesisParams = status_data_thesis(t);
    const statusPodcastParams = status_data_podcast(t);
    const statusVideoParams = status_data_video(t);
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const dispatch = useDispatch();
    const base_api = useSelector((state: RootState) => state.api.image_api);
    const hash_ids = new Hashids(process.env.REACT_APP_SECRET_SALT, 50);

    const category = searchParams.get('status');
    const category_title_name = library_category_name === ELibraryCategory.E_BOOK ?
        statusEbookParams?.find((x) => x.value === category)?.label :
        library_category_name === ELibraryCategory.MAGAZINE ?
            statusJournalParams?.find((x) => x.value === category)?.label :
            library_category_name === ELibraryCategory.THESIS ?
                statusJournalParams?.find((x) => x.value === category)?.label :
                library_category_name === ELibraryCategory.AUDIO_BOOK ?
                    statusAudioBookParams?.find((x) => x.value === category)?.label :
                    library_category_name === ELibraryCategory.PODCAST ?
                        statusPodcastParams?.find((x) => x.value === category)?.label :
                        library_category_name === ELibraryCategory.VIDEO ?
                            statusVideoParams?.find((x) => x.value === category)?.label :
                            statusParams?.find((x) => x.value === category)?.label;
    const category_status =
        library_category_name === ELibraryCategory.E_BOOK ?
            statusEbookParams?.find((x) => x.value === category)?.value as Mode | undefined :
            library_category_name === ELibraryCategory.THESIS ? statusThesisParams?.find((x) => x.value === category)?.value as Mode | undefined :
                library_category_name === ELibraryCategory.MAGAZINE ? statusJournalParams?.find((x) => x.value === category)?.value as Mode | undefined :
                    library_category_name === ELibraryCategory.PODCAST ? statusPodcastParams?.find((x) => x.value === category)?.value as Mode | undefined :
                        library_category_name === ELibraryCategory.VIDEO ? statusVideoParams?.find((x) => x.value === category)?.value as Mode | undefined :
                            statusParams?.find((x) => x.value === category)?.value as Mode | undefined

    const [pendingSearch, set_pendingSearch] = useState<string>('');
    const [search, set_search] = useState<string>('');

    const [library_type, set_library_type] = useState<any[]>([]);
    const [search_library_type, set_search_library_type] = useState<string>('');

    const [selected_library_type, set_selected_library_type] = useState<any>(null);
    const [id_library_type, set_id_library_type] = useState<any>(null);

    const [data, set_data] = useState<any[]>([]);
    const [page, set_page] = useState<number>(1);
    const [page_limit, set_page_limit] = useState<number>(10);
    const [total_items, set_total_items] = useState<number>(0);


    const [bookStatus, setBookStatus] = useState<string>(category || "ALL");
    const [borrowStatus, setBorrowStatus] = useState<string>("ALL");


    useEffect(() => {
        setBookStatus(category || "ALL");
    }, [category]);

    const sections: FilterSection[] = useMemo(() => {
        const isAll = bookStatus === "ALL";

        const commonCats = [
            ELibraryCategory.E_BOOK,
            ELibraryCategory.BOOK,
            ELibraryCategory.AUDIO_BOOK,
            ELibraryCategory.PODCAST,
            ELibraryCategory.VIDEO,
        ];

        const categoryOptions =
            commonCats.includes(library_category_name) && isAll
                ? [
                    {
                        label:
                            library_category_name === ELibraryCategory.E_BOOK
                                ? t("E-Book มาใหม่")
                                : library_category_name === ELibraryCategory.AUDIO_BOOK
                                    ? t("หนังสือเสียง มาใหม่")
                                    : library_category_name === ELibraryCategory.VIDEO
                                        ? t("วีดีโอ มาใหม่")
                                        : library_category_name === ELibraryCategory.PODCAST
                                            ? t("พอดแคสต์ มาใหม่")
                                            : t("หนังสือมาใหม่"),
                        value: "NEW",
                    },
                    {
                        label:
                            library_category_name === ELibraryCategory.E_BOOK
                                ? t("E-Book ยอดนิยม")
                                : library_category_name === ELibraryCategory.AUDIO_BOOK
                                    ? t("หนังสือเสียง ยอดนิยม")
                                    : library_category_name === ELibraryCategory.VIDEO
                                        ? t("วีดีโอ ยอดนิยม")
                                        : library_category_name === ELibraryCategory.PODCAST
                                            ? t("พอดแคสต์ ยอดนิยม")
                                            : t("หนังสือยอดนิยม"),
                        value: "POPULAR",
                    },
                    {
                        label:
                            library_category_name === ELibraryCategory.E_BOOK
                                ? t("E-Book ทั้งหมด")
                                : library_category_name === ELibraryCategory.AUDIO_BOOK
                                    ? t("หนังสือเสียง ทั้งหมด")
                                    : library_category_name === ELibraryCategory.VIDEO
                                        ? t("วีดีโอ ทั้งหมด")
                                        : library_category_name === ELibraryCategory.PODCAST
                                            ? t("พอดแคสต์ ทั้งหมด")
                                            : t("หนังสือทั้งหมด"),
                        value: "ALL",
                    },
                ]
                : [];

        return [
            {
                heading: t("สถานะหนังสือ"),
                value: bookStatus,
                onChange: (e: string) => {
                    // อัปเดต URL ให้ตรง (ยังคงทำได้เหมือนเดิม)
                    if (ELibraryCategory.E_BOOK === library_category_name) {
                        navigate({
                            pathname: routeName.e_library.e_books_list,
                            search: createSearchParams({ status: e }).toString(),
                        });
                    } else if (ELibraryCategory.BOOK === library_category_name) {
                        navigate({
                            pathname: routeName.e_library.books_list,
                            search: createSearchParams({ status: e }).toString(),
                        });
                    } else if (ELibraryCategory.AUDIO_BOOK === library_category_name) {
                        navigate({
                            pathname: routeName.e_library.audio_book_list,
                            search: createSearchParams({ status: e }).toString(),
                        });
                    } else if (ELibraryCategory.PODCAST === library_category_name) {
                        navigate({
                            pathname: routeName.e_library.podcast_list,
                            search: createSearchParams({ status: e }).toString(),
                        });
                    } else if (ELibraryCategory.VIDEO === library_category_name) {
                        navigate({
                            pathname: routeName.e_library.video_list,
                            search: createSearchParams({ status: e }).toString(),
                        });
                    }

                    // อัปเดต state ทันที → ทำให้ options เปลี่ยนทันรอบเรนเดอร์นี้
                    setBookStatus(e);
                },
                options: categoryOptions,
            },
            {
                heading: t("สถานะการยืม"),
                value: borrowStatus,
                onChange: setBorrowStatus,
                options: [
                    { label: t("ทั้งหมด"), value: "ALL" },
                    { label: t("พร้อมยืม"), value: "available" },
                ],
            },
        ];
    }, [bookStatus, library_category_name, borrowStatus]);

    const handleClear = () => {
        setBookStatus("all");
        setBorrowStatus("all");
    };


    const fetchLockRef = useRef(false);

    const fetchData = useCallback(async (not_loading?: boolean) => {
        if (!category_status) return;
        if (fetchLockRef.current) return;
        fetchLockRef.current = true;

        const query: listData = {
            page,
            limit: page_limit,
            library_category_name,
            ...(category_status !== 'ALL' ? { library_head_status: category_status } : {}),
            ...(search ? { search } : {}),
            ...(id_library_type?.id_library_type ? { id_library_type: id_library_type.id_library_type } : {}),
        };
        if (borrowStatus === 'available') query.ready_to_borrow = true
        try {
            if (!not_loading) {
                dispatch(setLoading());
            }
            const res = await ELibraryFrontApi.list(query);
            if (res.status < 300) {
                const items = res.data?.data?.items || [];
                const total = res.data?.data?.meta?.totalItems || 0;
                set_data(items);
                set_total_items(total);
            }
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(unsetLoading());
            fetchLockRef.current = false;
        }
    }, [category_status, borrowStatus, page, page_limit, search, id_library_type, dispatch, library_category_name]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleGetLibraryType = useCallback(async () => {
        let query: library_type_list = {
            page: 1,
            limit: 30,
            ...(search_library_type ? { search: search_library_type } : {})
        };

        try {
            const res = await LibraryTypeApi.list(query);
            if (res.status < 300) {
                set_library_type(res.data?.data?.items || []);
            }
        } catch (err) {
            console.log(err);
        }
    }, [borrowStatus]);

    useEffect(() => {
        handleGetLibraryType();
    }, [handleGetLibraryType]);

    useEffect(() => {
        if (!category_title_name) {
            navigate(-1);
        }
    }, []);

    return (
        <ContainerLayout
            title={category_title_name ?? ''}
            navigateContent={
                <NavigateListCustom
                    navigateData={[
                        { key: t('จัดการข้อมูล E-Library'), name: t('จัดการข้อมูล E-Library'), pathName: category_path, disabled_match: true },
                        { key: 'A', name: category_label, pathName: category_path },
                        { key: 'B', name: category_title_name ?? '', pathName: pathname },
                    ]}
                />
            }
            buttonContent={
                <BooksCart />
            }
            content={
                <Box sx={{ maxWidth: 1920, minWidth: "100%" }}>
                    <div className='flex w-full justify-between items-center'>
                        <Box className="flex justify-start items-center w-full">
                            <StateComponents
                                otherProps={{ spacing: 2 }}
                                sxContainer={{ maxWidth: "100%", width: '100%' }}
                                typeProps={[
                                    {
                                        seq: 1,
                                        type: 'TEXT_FIELD',
                                        gridProps: { xs: 12, md: 6, xl: 3 },
                                        fieldProps: {
                                            placeholder: t('ค้นหา ชื่อ'),
                                            startAdornment: (
                                                <Box mr={0.5}>
                                                    <icons.SearchNormal />
                                                </Box>
                                            ),
                                            value: pendingSearch,
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => set_pendingSearch(e.target.value || ''),
                                        },
                                    },
                                    {
                                        seq: 2,
                                        type: 'AUTO-COMPLETE',
                                        gridProps: { xs: 12, md: 6, xl: 3 },
                                        fieldProps: {
                                            getOptionLabel: (op: any) => op?.library_type_name || '',
                                            labelId: '12',
                                            options: library_type || [],
                                            value: selected_library_type || null,
                                            placeholder: t('ทุกหมวดหมู่'),
                                            onInputChange: (_: any, val: string) =>
                                                debouncedApiCall({ searchItem: val, setItem: set_search_library_type }),
                                            onChange: (_: any, val: any) => set_selected_library_type(val ?? null),
                                        },
                                    },
                                    {
                                        seq: 3,
                                        type: 'BUTTON',
                                        gridProps: { xs: 12, md: 6, xl: 3 },
                                        fieldProps: {
                                            sxContainer: { width: 'fit-content' },
                                            buttonData: [
                                                {
                                                    seq: 1,
                                                    key: 'SEARCH',
                                                    label: t('ค้นหา'),
                                                    type: 'SEARCH',
                                                    buttonProps: {
                                                        onClick: () => {
                                                            set_page(1);
                                                            set_search(pendingSearch);
                                                            set_id_library_type(selected_library_type);
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                ]}
                            />
                        </Box>
                        {[
                            ELibraryCategory.BOOK,
                            ELibraryCategory.E_BOOK,
                            ELibraryCategory.AUDIO_BOOK,
                            ELibraryCategory.VIDEO,
                            ELibraryCategory.PODCAST,
                        ].includes(library_category_name) && (
                                <Box className="flex-1 flex justify-end items-center">
                                    <PopoverFilter
                                        sections={sections}
                                        onApply={() => { }}
                                        onClear={handleClear}
                                        hidden_actions
                                        renderTrigger={({ onOpen }) => (
                                            <Button sx={{ backgroundColor: "#F2F4F7", borderRadius: "8px" }} onClick={onOpen} >
                                                <IconComponents.FilterSearchIcon />
                                            </Button>
                                        )}
                                    />
                                </Box>
                            )}
                    </div>

                    <Box minHeight={'60vh'} py={2}>
                        <BookSlide
                            title=""
                            subtitle=""
                            hidden_header
                            maxWidth={"100%"}
                            allow_shadow={allow_shadow}
                            breakpoints={breakpoints}
                            onViewAll={() => { }}
                            data={data?.map((list) => ({
                                onClick: (id) => {
                                    const encode_id = hash_ids.encode(String(id || ""));
                                    navigate({ pathname: path_detail.replace(":id", encode_id), search: `?${searchParams.toString()}` })
                                },
                                audio_popover: allow_view_audio ? (
                                    <AudioPopover
                                        audioUrl={""}
                                        only_view
                                    />
                                ) : undefined,
                                checkedLike: !!list?.is_like,
                                checkedBookmark: !!list?.is_bookmark,
                                setCheckedBookmark: async () => {
                                    await globalLikeBookmark(list?.id_library_head, LIBRARY_LIKE.BOOKMARK);
                                    fetchData(true);
                                },
                                setCheckedLike: async () => {
                                    await globalLikeBookmark(list?.id_library_head, LIBRARY_LIKE.LIKE);
                                    fetchData(true);
                                },
                                book_name: list?.library_head_name || '',
                                book_sub_name: list?.library_author_by || '',
                                id: list?.id_library_head,
                                img_url: `${base_api}/${list?.file_path}${list?.file_name}`,
                            }))}
                        />
                    </Box>

                    <CustomPagination
                        page={page}
                        pageLimit={page_limit}
                        totalItems={total_items}
                        setPage={(np) => {
                            set_page(np);
                        }}
                        setPageLimit={(npl) => {
                            set_page_limit(npl);
                            set_page(1);
                        }}
                    />
                </Box>
            }
        />
    );
};

export default BooksList;
